import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DataTable from '@repo/ui/data-table'
import { BuildingThreeIcon, ChevronLeftIcon } from '@repo/ui/icons'
import { LogSection } from '@repo/ui/log-section'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { getTeachingHallAssignments } from '../../../../api/ols/get-teaching-hall-assignments'
import { getTeachingHall } from '../../../../api/ols/teaching-halls/get-teaching-hall'
import { useNativeCountryName } from '../../../../lib/hooks/use-native-country-name'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import NotFoundPage from '../../../../views/common/not-found'
import DisplayImageInfo from '../../../elements/display-image-info'
import LabelWithText from '../../../elements/label-with-text'
import LoadingScreen from '../../../elements/loading/loading-screen'
import UserNavBar from '../../../elements/nav-bar'
import PhoneNumberDisplay from '../../../elements/phone-number-display'

const OlTeachingHallView = () => {
  const nativeCountryName = useNativeCountryName()
  const { back } = useNavigator()
  const { id, olId } = useParams<{ id: string; olId: string }>()

  const { data: teachingHall, isPending } = useQuery({
    queryKey: ['teaching-hall', id],
    queryFn: () => {
      if (!id || isNaN(+id) || !olId) return
      return getTeachingHall({ id: +id, olId: +olId })
    }
  })

  const { data: assignments } = useQuery({
    queryKey: ['teaching-hall-assignments', id],
    queryFn: () => getTeachingHallAssignments({ olId: Number(olId), teachingHallId: Number(id) }),
    enabled: !!id && !isNaN(+id) && !!olId
  })

  if (isPending) return <LoadingScreen />
  if (!teachingHall) return <NotFoundPage />

  return (
    <div className="flex flex-col gap-4">
      <UserNavBar title={teachingHall.name} icon={BuildingThreeIcon} />
      <Box className="flex flex-col gap-4">
        <Button
          icon={ChevronLeftIcon}
          label="Retour"
          variant="ghost"
          className="border-grey-light w-fit"
          onClick={() => back()}
        />

        <div className="grid grid-cols-3 gap-4 mobile:grid-cols-2 tab:grid-cols-2 smallLaptop:grid-cols-2">
          <div className="col-span-2">
            <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
              <Typography variant={'text'} color={tailwindColors.primary.DEFAULT}>
                Adresse
              </Typography>
              <div className="h-4" />
              <div className="grid grid-cols-4 gap-2 mobile:grid-cols-1 tab:grid-cols-1 smallLaptop:grid-cols-2">
                <LabelWithText label={'Nom de la salle de cours'} data={teachingHall.name} />
                <LabelWithText
                  label={'Nombre de salles de classe'}
                  data={teachingHall.numberOfClassrooms.toString()}
                />
                <LabelWithText
                  display="column"
                  label="Adresse"
                  data={
                    <AddressLink
                      address1={teachingHall.address1}
                      city={teachingHall.city}
                      country={nativeCountryName(teachingHall.country)}
                    />
                  }
                />
              </div>
              <div className="h-4" />
              <div className="grid grid-cols-4 gap-2 mobile:grid-cols-1 tab:grid-cols-1 smallLaptop:grid-cols-2">
                <LabelWithText
                  label="Complément d'adresse"
                  data={valueRender(teachingHall.address2)}
                  className="flex-1"
                />
                <LabelWithText label="Ville" data={teachingHall.city} className="flex-1" />
                <LabelWithText
                  label="Code postal"
                  data={teachingHall.postalCode}
                  className="flex-1"
                />
                <LabelWithText
                  label="Pays"
                  data={nativeCountryName(teachingHall.country)}
                  className="flex-1"
                />
              </div>
            </Box>
            <div className="h-4" />
            <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
              <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                Coordonnées du contact
              </Typography>
              <div className="h-4" />
              <div className="grid grid-cols-4 mobile:grid-cols-1 tab:grid-cols-1 smallLaptop:grid-cols-2 gap-2">
                <LabelWithText label="NOM" data={teachingHall.contactLastName.toUpperCase()} />
                <LabelWithText
                  label="Prénom"
                  data={StringFormatter.capitalize(teachingHall.contactFirstName)}
                />
                <LabelWithText
                  label="Email"
                  data={teachingHall.contactEmail}
                  className="break-all text-balance"
                />
                <LabelWithText
                  label="Numéro de téléphone"
                  data={<PhoneNumberDisplay phoneNumber={teachingHall.contactPhone} />}
                />
              </div>
            </Box>
          </div>

          <div className={'mobile:col-span-2 tab:col-span-2 smallLaptop:col-span-2'}>
            <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
              <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                Photos du centre de cours
              </Typography>
              <div className="h-3" />

              <div className="h-3" />
              <div className="flex flex-col gap-3">
                {teachingHall.files.map((file, index) => (
                  <DisplayImageInfo key={index} file={file} />
                ))}
              </div>
            </Box>

            <div className="h-4" />

            <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
              <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
                Quotations
              </Typography>
              <div className="h-3" />

              <div className="h-3" />
              <div className="flex flex-col gap-3">
                {teachingHall.quotations?.map((quotation, index) => (
                  <DisplayImageInfo
                    key={index}
                    file={quotation.file}
                    label={quotation.name}
                    informationSection={
                      <Badge
                        variant="grey"
                        text={`Valide jusqu'à : ${DateFormatter.DDMMYYYY(new Date(quotation.validUntil))}`}
                      />
                    }
                  />
                ))}
              </div>
            </Box>
          </div>

          <Box className="bg-white-hover w-full rounded-lg border border-grey-light col-span-3 mobile:col-span-2 tab:col-span-2 smallLaptop:col-span-2">
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Historique des séjours
            </Typography>
            <div className="h-3" />

            <div className="h-3" />
            <DataTable
              data={assignments ?? []}
              bodyVariant="white"
              columns={[
                {
                  header: 'De',
                  accessorKey: 'from',
                  enableSorting: true,
                  cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.from)} />
                },
                {
                  header: 'À',
                  accessorKey: 'to',
                  enableSorting: true,
                  cell: ({ row }) => <Badge text={DateFormatter.DDMMYYYY(row.original.to)} />
                },
                {
                  header: 'Formules',
                  accessorKey: 'formulas',
                  cell: ({ row }) =>
                    row.original.formulas.map((formula) => getTranslatedName(formula)).join(', ')
                },
                {
                  header: 'Prix par jour',
                  accessorKey: 'pricePerDay',
                  enableSorting: true,
                  cell: ({ row }) =>
                    row.original.pricePerDay != null
                      ? NumberFormatter.formatCurrency(
                          row.original.pricePerDay,
                          row.original.currency
                        )
                      : '-'
                }
              ]}
              rowCount={0}
              paginated={false}
              manualSorting={false}
            />
          </Box>
        </div>
      </Box>

      <Box className="w-full rounded-3xlx border border-grey-light">
        <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
          <Typography variant="text">Commentaires</Typography>
          <div className="h-3" />

          <Box className="bg-white-hover w-full rounded-lg border border-grey-light">
            <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
              Commentaires OL
            </Typography>
            <div className="h-3" />

            <LogSection
              logItemClassName="bg-white border border-grey-light"
              data={teachingHall.comments.map((comment) => ({
                comment: comment.comment,
                updatedAt: new Date(comment.updatedAt)
              }))}
              dateFormatter={(date) => DateFormatter.ddMMyyyyHHmm(date)}
            />
          </Box>
        </Box>
      </Box>
    </div>
  )
}

export default OlTeachingHallView
